import type React from 'react'
import { InputWithSuffix } from '@components/ui/input-group'
import { Slider } from '@components/ui/slider'
import { ToggleGroup, ToggleGroupItem } from '@components/ui/toggle-group'
import { cn } from '@/utils'
import { keepTextEditFocus } from '../handlers/keep-text-edit-focus'
import { resolveSpacingPlaceholder } from '../helpers/spacing-input'
import { FieldError } from './field-error'

interface SpacingFieldProps {
  label: string
  /** Labels the chips and input by the visible label (their accessible name). */
  labelId?: string
  disabled: boolean
  /** Also block pointer events on the whole section while disabled. Tracking leaves this off: it
   *  relies on its controls' own `disabled` prop, so a CSS re-render on this container can never
   *  interrupt an in-progress Slider drag on one of its descendants. */
  blockPointerWhenDisabled: boolean
  chipOptions: readonly string[]
  chipValue: string
  onChipChange: (value: string) => void
  sliderValue: number[]
  sliderMin: number
  sliderMax: number
  sliderStep: number
  onSliderKeyDown: (e: React.KeyboardEvent) => void
  onSliderChange: (value: number[]) => void
  onSliderCommit: () => void
  thumbLabel?: string
  thumbValueText?: string
  /** Marks the slider row keepalive (Leading) — see the call site. */
  sliderRowKeepalive?: boolean
  inputValue: string
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode']
  mixed: boolean
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onInputKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onInputFocus: (e: React.FocusEvent<HTMLInputElement>) => void
  onInputBlur: (e: React.FocusEvent<HTMLInputElement>) => void
  error: boolean
  errorMessage: string
  errorId: string
  /** Unit shown after the value; omitted when undefined. */
  suffix?: string
}

/** One spacing control (Leading, Kerning or Tracking): preset chips, slider, input and error. */
export function SpacingField({
  label,
  labelId,
  disabled,
  blockPointerWhenDisabled,
  chipOptions,
  chipValue,
  onChipChange,
  sliderValue,
  sliderMin,
  sliderMax,
  sliderStep,
  onSliderKeyDown,
  onSliderChange,
  onSliderCommit,
  thumbLabel,
  thumbValueText,
  sliderRowKeepalive,
  inputValue,
  inputMode,
  mixed,
  onInputChange,
  onInputKeyDown,
  onInputFocus,
  onInputBlur,
  error,
  errorMessage,
  errorId,
  suffix,
}: SpacingFieldProps) {
  return (
    <div
      className={cn(
        'flex flex-col gap-2',
        disabled && (blockPointerWhenDisabled ? 'pointer-events-none opacity-60' : 'opacity-60')
      )}
      onPointerDown={keepTextEditFocus}
    >
      <label id={labelId} className="text-sm font-medium text-foreground leading-normal">
        {label}
      </label>

      <ToggleGroup
        type="single"
        aria-labelledby={labelId}
        value={chipValue}
        onValueChange={onChipChange}
        disabled={disabled}
        className="justify-start gap-2"
      >
        {/* Toggle's default size (36px tall, 8px padding, 14px text) is the Figma chip spec. */}
        {chipOptions.map((option) => (
          <ToggleGroupItem key={option} value={option} className="font-medium leading-5">
            {option}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>

      <div
        className="flex items-center gap-[1.125rem]"
        data-text-edit-keepalive={sliderRowKeepalive ? '' : undefined}
      >
        <Slider
          value={sliderValue}
          onKeyDown={onSliderKeyDown}
          onValueChange={onSliderChange}
          onValueCommit={onSliderCommit}
          disabled={disabled}
          min={sliderMin}
          max={sliderMax}
          step={sliderStep}
          className="flex-1 min-w-0 max-w-[8.125rem]"
          trackClassName="bg-muted"
          thumbClassName="border-primary"
          thumbLabel={thumbLabel}
          thumbValueText={thumbValueText}
        />

        <InputWithSuffix
          type="text"
          inputMode={inputMode}
          aria-labelledby={labelId}
          // Design-system "has a committed value" marker — same convention the X/Y/W/H
          // inputs (`object-panel/components/transform-field.tsx`) use; no separate styling
          // approach introduced.
          data-filled={inputValue !== '' ? true : undefined}
          value={inputValue}
          disabled={disabled}
          placeholder={resolveSpacingPlaceholder(inputValue, mixed)}
          onChange={onInputChange}
          onKeyDown={onInputKeyDown}
          onFocus={onInputFocus}
          onBlur={onInputBlur}
          size="compact"
          className={cn(
            'text-sm',
            // A filled value uses the same `foreground` token as the X/Y/W/H inputs; an invalid one
            // is muted, per the Figma error state.
            inputValue !== '' && (error ? 'text-muted-foreground' : 'text-foreground'),
            mixed && inputValue === '' && !disabled && 'placeholder:text-base-foreground'
          )}
          error={error}
          aria-describedby={error ? errorId : undefined}
          suffix={
            suffix ? (
              <span className="text-xs text-muted-foreground pl-1">{suffix}</span>
            ) : undefined
          }
        />
      </div>

      {/* Its own full-width row beneath the whole control (right-aligned, per Figma) rather than
          under the input, where a message wider than the input widened that column and shifted the
          slider. Shown only while there is an error, pushing the content below down — Design's
          call for now, pending an app-wide error-message sweep. */}
      {error && (
        <div className="flex justify-end pr-[0.5625rem]" data-slot="spacing-error-row">
          <FieldError id={errorId} className="mt-0 whitespace-nowrap text-sm font-medium leading-5">
            {errorMessage}
          </FieldError>
        </div>
      )}
    </div>
  )
}
